import {
  ListTypeEnum,
  TSessionListGroupTitle,
  TSessionListItem,
} from '../../type';
import { SessionItem } from '../session-item/index.js';
import InfiniteScroll from 'react-infinite-scroll-component';
import './index.less';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Input, Loading, modalConfirm, ModalWithMask } from '@kim/ui';
import { useAIWindowContext } from '../../../Context/context.js';
import { groupConversionByTime } from '../../utils.js';
import classNames from 'classnames';
import intl from '@/internationalzation/intl';
import '../../../Claw/chat/SessionsPopover.less';
import { isMainagentPath } from '@src/utils/mainagent-route';

export const SessionList = () => {
  const location = useLocation();
  const isClawRoute = isMainagentPath(location.pathname);
  const {
    app,
    conversionId,
    isDarkMode,
    toConversion,
    loadConversations,
    conversationList,
    conversationPageInfo,
    deleteConversation,
    renameConversation,
    shareConversation,
    unreadConversationIds,
  } = useAIWindowContext();
  const [data, setData] = useState<
    Array<TSessionListItem | TSessionListGroupTitle>
  >([]);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const renameInfoRef = useRef<{
    id: string;
    name: string;
  }>({
    id: '',
    name: '',
  });
  const [renameInputValue, setRenameInputValue] = useState('');

  const onSessionClick = useCallback(
    (id: string) => {
      toConversion(id);
    },
    [toConversion],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!id) return;
      modalConfirm({
        content: intl.t('mario.delete.confirm'),
        okText: intl.t('mario.button.delete'),
        cancelText: intl.t('mario.button.cancel'),
        confirmBtnType: 'danger',
        onOK: async () => {
          deleteConversation(id);
        },
      });
    },
    [deleteConversation],
  );

  const handleRenameStart = useCallback(async (id: string, name: string) => {
    if (!id) return;
    renameInfoRef.current = {
      id,
      name,
    };
    setRenameInputValue(name);
    setRenameModalVisible(true);
  }, []);
  // 分享
  const handleShare = useCallback(
    (id: string) => {
      if (!id) return;
      shareConversation(id);
    },
    [shareConversation],
  );

  const handleRenameOk = useCallback(() => {
    const value = renameInputValue.trim();
    if (!value || value === renameInfoRef.current.name) {
      setRenameModalVisible(false);
      return;
    }
    renameConversation(renameInfoRef.current.id, value);
    setRenameModalVisible(false);
  }, [renameConversation, renameInputValue]);

  const handleRenameCancel = useCallback(() => {
    setRenameModalVisible(false);
    renameInfoRef.current = {
      id: '',
      name: '',
    };
  }, []);

  const itemRenderer = useCallback(
    (item: TSessionListItem | TSessionListGroupTitle, index: number) => {
      if (item.type === ListTypeEnum.TITLE) {
        return (
          <div
            className="kim-ai-mario-sidebar-session-group-title"
            data-title={item.title}
            key={`group-${item.title}`}
          >
            {item.title}
          </div>
        );
      }
      return (
        <SessionItem
          title={item.conversationName || ''}
          id={item.conversationId || ''}
          icon={isDarkMode ? item.icon?.dark || '' : item.icon?.light || ''}
          editable={true}
          active={conversionId === item.conversationId}
          key={`item-${item.conversationId}`}
          status={item.status}
          onClick={onSessionClick}
          onDelete={handleDelete}
          onRename={handleRenameStart}
          onShare={isClawRoute ? undefined : handleShare}
          hasUnread={unreadConversationIds.includes(item.conversationId || '')}
        />
      );
    },
    [
      onSessionClick,
      handleDelete,
      handleRenameStart,
      handleShare,
      isClawRoute,
      conversionId,
      isDarkMode,
      toConversion,
      unreadConversationIds,
    ],
  );

  useEffect(() => {
    if (!conversationList || !conversationList.length) {
      setData([]);
      return;
    }
    setData(groupConversionByTime(conversationList));
  }, [conversationList]);

  return (
    <div className="kim-ai-mario-sidebar-session-list-wrapper sessions-popover sessions-popover--sidebar-embed">
      <div
        className="kim-ai-mario-sidebar-session-list-content sessions-popover__body"
        id="kim-ai-mario-sidebar-session-list-content"
      >
        {data.length > 0 ? (
          <InfiniteScroll
            dataLength={data.length}
            next={() => loadConversations(false, conversationPageInfo)}
            hasMore={conversationPageInfo.hasMore}
            loader={<Loading className="mario-loading center" size={16} />}
            scrollableTarget="kim-ai-mario-sidebar-session-list-content"
          >
            {data.map((item, index) => itemRenderer(item, index))}
          </InfiniteScroll>
        ) : (
          <div className="kim-ai-mario-sidebar-list-empty">
            {intl.t('mario.task.null')}
          </div>
        )}
      </div>

      {renameModalVisible && (
        <ModalWithMask
          visible={true}
          className={classNames('mario-rename-modal', {
            disabled: !renameInputValue.trim(),
          })}
          title={intl.t('mario.button.rename')}
          okText={intl.t('Sure')}
          cancelText={intl.t('mario.button.cancel')}
          onOK={handleRenameOk}
          onCancel={handleRenameCancel}
        >
          <Input
            autoFocus={true}
            value={renameInputValue}
            onChange={(e) => {
              setRenameInputValue(e.target.value);
            }}
            type="text"
            maxLength={50}
            className="mario-rename-input"
          />
        </ModalWithMask>
      )}
    </div>
  );
};
